<script setup lang="ts">
import { computed } from 'vue'

import type { AlertDraft } from '../models/alert.types'
import type { AlertReportLevel } from '../models/reports-bridge'
import type { AlertValidationKey } from '../service/alertValidator'

import { toReportColumnOption } from '@/features/reports'
import { useI18n } from '@/shared/i18n'
import { Input, MultiSelect, Select } from '@/shared/ui'

const props = defineProps<{
  draft: AlertDraft
  levels: readonly AlertReportLevel[]
  fieldsLoading: boolean
  error: AlertValidationKey | null
  errors?: readonly AlertValidationKey[]
  readOnly: boolean
}>()
const emit = defineEmits<{
  'update:name': [value: string]
  'update:level': [value: AlertReportLevel | null]
  'update:groupBy': [fields: string[]]
}>()
const { t } = useI18n()

const levelOptions = computed(() =>
  props.levels.map((level) => ({ value: level.partition_name, label: level.name })),
)
const dimensionOptions = computed(() =>
  (props.draft.level?.tableDimensions ?? []).map((column) => ({
    ...toReportColumnOption(column),
  })),
)
const selectedLevel = computed<string | null>({
  get: () => props.draft.level?.partition_name ?? null,
  set: (partition) =>
    emit('update:level', props.levels.find((level) => level.partition_name === partition) ?? null),
})
const selectedDimensions = computed<string[]>({
  get: () => props.draft.groupBy.map((column) => column.field),
  set: (fields) => emit('update:groupBy', fields),
})
const nameError = computed(() => (props.errors ?? [props.error]).includes('alerts.validation.name'))
const levelError = computed(() =>
  (props.errors ?? [props.error]).includes('alerts.validation.level'),
)
</script>

<template>
  <div class="alert-step grid content-start gap-lg py-sm">
    <Input
      :model-value="draft.name"
      :label="t('alerts.fields.name')"
      :placeholder="t('alerts.fields.namePlaceholder')"
      :disabled="readOnly"
      :error="nameError"
      :error-message="t('alerts.validation.name')"
      @update:model-value="emit('update:name', String($event ?? ''))"
    />
    <Select
      v-model="selectedLevel"
      :options="levelOptions"
      :label="t('alerts.fields.level')"
      :placeholder="t('alerts.fields.levelPlaceholder')"
      searchable
      :disabled="readOnly"
      :state="levels.length ? 'ready' : 'empty'"
    />
    <p
      v-if="levelError"
      class="alert-step__error mt-[calc(var(--ui-space-md)_*_-1)] mx-0 mb-0 text-danger text-[0.75rem]"
    >
      {{ t(error!) }}
    </p>
    <MultiSelect
      v-model="selectedDimensions"
      :options="dimensionOptions"
      :label="t('alerts.fields.groupBy')"
      :placeholder="t('alerts.fields.groupByPlaceholder')"
      searchable
      :state="fieldsLoading ? 'loading' : 'ready'"
      :disabled="readOnly || !draft.level"
    />
  </div>
</template>
